import { deltaE2000 } from './deltaE2000'
import type { Lab } from './types'

export type { DeltaEWeights, Lab, Rgb } from './types'
export { deltaE2000, deltaE76 } from './deltaE2000'
export {
  hexToRgb,
  labToHex,
  labToSrgb,
  readableTextColour,
  rgbToHex,
  srgbToLab,
  type SrgbResult,
} from './labToSrgb'

export interface NearestMatch<T> {
  item: T
  deltaE: number
}

/**
 * Rank candidates by CIEDE2000 distance from a target colour.
 *
 * A linear scan: at the few hundred published records DyeAtlas holds this costs
 * well under a millisecond. If the public database grows past roughly 50k records,
 * pre-filter in SQL (a bounding box on L*, a*, b*) before calling this.
 */
export function nearestByDeltaE<T>(
  target: Lab,
  candidates: readonly T[],
  getLab: (candidate: T) => Lab | null,
  limit = 5,
): NearestMatch<T>[] {
  const scored: NearestMatch<T>[] = []

  for (const item of candidates) {
    const lab = getLab(item)
    if (!lab) continue
    scored.push({ item, deltaE: deltaE2000(lab, target) })
  }

  scored.sort((x, y) => x.deltaE - y.deltaE)
  return scored.slice(0, limit)
}

/**
 * How a colour difference reads to a human eye, for labelling ΔE values in the UI.
 * Thresholds follow the usual textile and print convention.
 */
export function describeDeltaE(deltaE: number): string {
  if (deltaE < 1) return 'Not perceptible'
  if (deltaE < 2) return 'Perceptible on close inspection'
  if (deltaE < 10) return 'Perceptible at a glance'
  if (deltaE < 50) return 'Clearly different'
  return 'Opposite colours'
}
